import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

/*
 * Loading states in the shape of what is coming, so a route change does not
 * blank the screen and the layout does not jump when the data lands. Each one
 * is a `status` with the same spoken label the spinner it replaced had.
 */

function Loading({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div role="status" className={className}>
      <span className="sr-only">{label}</span>
      <div aria-hidden="true" className="contents">
        {children}
      </div>
    </div>
  )
}

/** A table or list that has not arrived yet. */
export function LoadingRows({
  label = 'Loading',
  rows = 6,
  className,
}: {
  label?: string
  rows?: number
  className?: string
}) {
  return (
    <Loading label={label} className={cn('flex flex-col', className)}>
      <Skeleton className="mb-1 h-8 w-full opacity-60" />
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="border-foreground/8 flex items-center gap-3 border-b py-2.5">
          <Skeleton className="size-8 shrink-0" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-3 w-1/4 opacity-70" />
          </div>
          <Skeleton className="h-4 w-12" />
        </div>
      ))}
    </Loading>
  )
}

/** A few lines of text: a pane, a dialog body. */
export function LoadingLines({
  label = 'Loading',
  lines = 3,
  className,
}: {
  label?: string
  lines?: number
  className?: string
}) {
  return (
    <Loading label={label} className={cn('flex flex-col gap-2.5 py-2', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className="h-4"
          style={{ width: `${[85, 60, 72, 45, 66][index % 5]}%` }}
        />
      ))}
    </Loading>
  )
}

/** A whole detail page: a plate of facts over a table. */
export function LoadingPage({ label = 'Loading' }: { label?: string }) {
  return (
    <Loading label={label} className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} className="h-20" />
        ))}
      </div>
      <LoadingRowsInner rows={5} />
    </Loading>
  )
}

function LoadingRowsInner({ rows }: { rows: number }) {
  return (
    <div className="flex flex-col">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="border-foreground/8 flex items-center gap-3 border-b py-2.5">
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-1/3" />
            <Skeleton className="h-3 w-1/5 opacity-70" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  )
}
